const EMOTION_ORDER = ['happy', 'sad', 'angry', 'fear', 'disgust', 'surprise'];

export const WEEKLY_EMOTION_META = {
  happy: { label: 'Happy', color: '#f5c451', emoji: '😊', tone: 'positive' },
  sad: { label: 'Sad', color: '#6f8de8', emoji: '😢', tone: 'negative' },
  angry: { label: 'Angry', color: '#f17b68', emoji: '😡', tone: 'negative' },
  fear: { label: 'Fear', color: '#ad82df', emoji: '😨', tone: 'negative' },
  disgust: { label: 'Disgust', color: '#70b890', emoji: '🤢', tone: 'negative' },
  surprise: { label: 'Surprise', color: '#57c6d9', emoji: '😮', tone: 'positive' },
};

function parseEmotionString(value) {
  const result = {};
  String(value || '').split(',').forEach((part) => {
    const match = part.trim().match(/^([a-z]+)\s+([\d.]+)%?$/i);
    if (match) result[match[1].toLowerCase()] = Number(match[2]) || 0;
  });
  return result;
}

export function parseWeeklyEmotions(value) {
  const parsed = typeof value === 'string' ? parseEmotionString(value) : value || {};
  return EMOTION_ORDER.reduce((result, key) => {
    result[key] = Number(parsed[key]) || 0;
    return result;
  }, {});
}

function emotionsFromRecord(record) {
  return parseWeeklyEmotions(record?.response?.RES?.top_5 || record?.top_5);
}

function average(values) {
  return values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : null;
}

export function buildWeeklySeries(records = [], startDate, endDate) {
  if (!startDate) return [];
  const start = new Date(`${startDate}T00:00:00`);
  const days = Array.from({ length: 7 }, (_, index) => {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    return date;
  });

  return days.map((date) => {
    const key = date.toISOString().slice(0, 10);
    const dayRecords = records.filter((record) => (
      (record?.report_date || record?.created_at || '').slice(0, 10) === key
    ));
    const dailyValues = dayRecords.map(emotionsFromRecord);
    const moodValues = dailyValues.map((emotion) => {
      const positive = emotion.happy + emotion.surprise;
      const total = Object.values(emotion).reduce((sum, value) => sum + value, 0);
      return total > 0 ? (positive / total) * 100 : null;
    }).filter((value) => value != null);

    return {
      date: key,
      day: date.toLocaleDateString('en-US', { weekday: 'short' }),
      shortDate: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      sessions: dayRecords.length,
      mood: average(moodValues),
    };
  });
}

export function getWeeklyMetrics(aggregate, records = []) {
  const aggregateEmotions = parseWeeklyEmotions(aggregate?.top_5);
  const sessions = records.map(emotionsFromRecord);
  const source = sessions.length ? sessions : [aggregateEmotions];
  const totals = EMOTION_ORDER.reduce((result, key) => {
    result[key] = average(source.map((emotion) => emotion[key])) || 0;
    return result;
  }, {});
  const total = Object.values(totals).reduce((sum, value) => sum + value, 0);
  const distribution = EMOTION_ORDER.map((key) => ({
    key,
    ...WEEKLY_EMOTION_META[key],
    value: totals[key],
    percentage: total > 0 ? (totals[key] / total) * 100 : 0,
  }));
  const dominant = distribution.reduce((current, emotion) => (
    emotion.value > current.value ? emotion : current
  ), distribution[0]);
  const positive = distribution
    .filter((emotion) => emotion.tone === 'positive')
    .reduce((sum, emotion) => sum + emotion.value, 0);
  const negative = distribution
    .filter((emotion) => emotion.tone === 'negative')
    .reduce((sum, emotion) => sum + emotion.value, 0);
  const balanceTotal = positive + negative;

  return {
    distribution: distribution.filter((emotion) => emotion.value > 0),
    dominant,
    positivePercentage: balanceTotal ? (positive / balanceTotal) * 100 : null,
    negativePercentage: balanceTotal ? (negative / balanceTotal) * 100 : null,
    totalSessions: records.length,
  };
}

export function formatWeekRange(startDate, endDate) {
  if (!startDate || !endDate) return 'This week';
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  const format = (date, options) => date.toLocaleDateString('en-US', options);
  return `${format(start, { month: 'short', day: 'numeric' })} – ${format(end, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })}`;
}
